import {useRef} from 'react';
import dayjs from 'dayjs';
import cx from 'classnames';

import sdk from '@/sdk';
import Tooltip from '@/components/Tooltip';
import {EDITOR_MODE} from '@/Constant';
import {GRAY_RELEASE_KEYS, initGray} from '@/grayRelease';
import DropMenu, {DropMenuTrigger, DropMenuValue} from '@/components/DropMenu';
import CopyIcon from '@/assets/svg/material/copy.svg?tsx';
import InsertIcon from '@/assets/svg/material/insert.svg?tsx';
import PreviewIcon from '@/assets/svg/material/preview.svg?tsx';
import ViewOriginIcon from '@/assets/svg/material/viewOrigin.svg?tsx';
import TopIcon from '@/assets/svg/material/top.svg?tsx';
import CancelTopIcon from '@/assets/svg/material/cancelTop.svg?tsx';
import DeleteIcon from '@/assets/svg/material/delete.svg?tsx';
import MoreIcon from '@/assets/svg/material/more.svg?tsx';
import EditIcon from '@/assets/svg/material/edit.svg?tsx';
import TimeIcon from '@/assets/svg/material/time.svg?tsx';
import {ENVIRONMENT_LEVEL} from '@/Constant';
import {getMorphoService, serviceNames} from '@/rte/editor';
import {
    reportClickMaterialCopy,
    reportClickViewOrigin,
    reportClickTop,
    reportClickInsert,
} from '@/buried/material';

import {IListItem, MaterialTab} from '../../../interface';
import styles from './index.module.scss';

function formatTime(time: number): string {
    const timeObj = dayjs(time);
    if (!timeObj.isValid()) {
        return '';
    }

    const isCurYear = timeObj.year() === dayjs().year();
    const formatString = isCurYear ? 'MM-DD HH:mm:ss' : 'YYYY-MM-DD HH:mm:ss';

    return `保存于${timeObj.format(formatString)}`;
}

function getIsWikiAdmin(userGuid: string | undefined) {
    if (!userGuid) {
        return false;
    }

    return ['zfXMQb3fe2', 'rFPfOuKCii'].includes(userGuid);
}

enum MoreToolValue {
    Top = 'top',
    Origin = 'origin',
    Delete = 'delete',
}

interface IProps {
    data: IListItem;
    isOfficial?: boolean;
    onPreview: (id: string, content: any[]) => void;
    onTop: (data: IListItem) => void;
    onDelete: (id: string) => void;
    onEdit: (data: IListItem) => void;
    documentMode: EDITOR_MODE;
    selectTab: MaterialTab;
    setIsHover: (value: boolean) => void;
}

const CardTool: React.FC<IProps> = ({
    isOfficial = false,
    data,
    onPreview,
    onTop,
    onDelete,
    onEdit,
    documentMode,
    selectTab,
    setIsHover,
}) => {
    const topTooltipRef = useRef<any>(null);
    const {isTop = false, content, docGuid, id, createdTime, name, isNew} = data;
    const formatCreatedTime = formatTime(createdTime);
    const isWikiAdmin = getIsWikiAdmin(sdk.user?.userGuid); // 是否为 wiki_admin 账户
    const isEdit = [EDITOR_MODE.edit, EDITOR_MODE.meeting].includes(documentMode);

    const handleCopy = () => {
        const materialConfig = getMorphoService(serviceNames.IMaterialService);
        if (materialConfig && materialConfig.setMaterialToClipBoard) {
            materialConfig.setMaterialToClipBoard(content, {
                id,
                source: selectTab,
                name,
            });

            reportClickMaterialCopy({
                id,
                source: selectTab,
            });
        }
    };

    const handleInsert = async () => {
        try {
            // 添加nodes参数，用于埋点
            const editor = getMorphoService(serviceNames.IEditorStateService).editor;
            const nodes = editor.commonUtils.calcNodeWithTypes(editor, {children: content}, {
                distinguishBlockLayout: true,
                serializeToString: true,
            });
            const materialConfig = getMorphoService(serviceNames.IMaterialService);
            reportClickInsert({
                id,
                source: selectTab,
                name,
                action: 'insert',
                docId: docGuid,
                isNew,
                nodes: nodes,
            });

            if (materialConfig && materialConfig.applyMaterial) {
                materialConfig.applyMaterial(content, {
                    source: selectTab,
                });
                const ksServer = await sdk.getKsServer();
                ksServer.matterApply({
                    docGuid: docGuid,
                    matterGuid: id,
                });
            }
        }
        catch (e) {
            console.error(e);
        }
    };

    const handlePreview = () => {
        onPreview(id, content);
    };

    const handleViewOrigin = () => {
        let origin = window.location.origin;
        if (process.env.REACT_APP_ENVIRONMENT === ENVIRONMENT_LEVEL.local) {
            origin = 'https://ku.dev.weiyun.baidu.com';
        }
        const shortUrl = `${origin}/d/${docGuid}`;
        window.open(shortUrl);

        reportClickViewOrigin({id});
    };

    const handleTop = () => {
        onTop(data);

        if (topTooltipRef.current?.refTrigger?.current?.setVisible) {
            topTooltipRef.current?.refTrigger?.current?.setVisible(false);
        }

        reportClickTop({
            id,
            action: isTop ? 'cancelTop' : 'top',
        });
    };

    const handleDelete = () => {
        onDelete(id);
    };

    const handleEdit = () => {
        onEdit(data);
    };

    const handleClickMenu = (val: DropMenuValue) => {
        if (val === MoreToolValue.Top) {
            handleTop();
        } else if (val === MoreToolValue.Origin) {
            handleViewOrigin();
        } else if (val === MoreToolValue.Delete) {
            handleDelete();
        }
        setIsHover(false);
    };

    const moreToolOptions = [
        {
            value: MoreToolValue.Top,
            label: (
                <div className={styles.moreItem}>
                    {isTop ? <CancelTopIcon /> : <TopIcon />}
                    {isTop ? '取消置顶' : '置顶'}
                </div>
            ),
        },
        {value: MoreToolValue.Origin, label: <div className={styles.moreItem}><ViewOriginIcon />访问原始文档</div>},
        {value: MoreToolValue.Delete, label: <div className={styles.moreItem}><DeleteIcon />删除</div>},
        {
            value: 'group',
            label: (
                <div className={styles.time}>
                    <TimeIcon />
                    <span className={styles.text}>{formatCreatedTime}</span>
                </div>
            ),
            children: [],
        },
    ];

    return (
        <div className={styles.container} onClick={(e) => e.stopPropagation()}>
            <div className={styles.toolbar}>
                {isWikiAdmin && (
                    <Tooltip
                        placement={'top'}
                        content="编辑"
                    >
                        <div className={cx(styles.icon, styles.edit)} onClick={handleEdit}><EditIcon /></div>
                    </Tooltip>
                )}
                {isEdit && (
                    <Tooltip
                        placement={'top'}
                        content="插入"
                    >
                        <div className={styles.icon} onClick={handleInsert}><InsertIcon /></div>
                    </Tooltip>
                )}
                {/* <Tooltip
                    placement={'top'}
                    content="复制"
                >
                    <div className={styles.icon} onClick={handleCopy}><CopyIcon /></div>
                </Tooltip> */}
                <Tooltip
                    placement={'top'}
                    content="预览"
                >
                    <div className={styles.icon} onClick={handlePreview}><PreviewIcon /></div>
                </Tooltip>
                {selectTab === MaterialTab.Mine && (
                    <DropMenu
                        className={styles.moreMenu}
                        triggerType={DropMenuTrigger.Click}
                        options={moreToolOptions}
                        value={null}
                        onChange={handleClickMenu}
                    >
                        <div className={styles.icon}><MoreIcon /></div>
                    </DropMenu>
                )}
            </div>
        </div>
    );
};

export default CardTool;
